/**
 * HW Media Hotspots - JS
 * Extracted from blocks/hw-media-hotspots.liquid
 */

const mediaQueryLarge = window.matchMedia('(min-width: 750px)');
const isTouchDevice = () =>
  window.matchMedia('(hover: none)').matches ||
  navigator.maxTouchPoints > 0;

class HwMediaHotspotComponent extends HTMLElement {
  constructor() {
    super();
    this.timer = null;
    this.pointerenterHandler = null;
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.lightDismissMouse = this.lightDismissMouse.bind(this);
    this.lightDismissKeyboard = this.lightDismissKeyboard.bind(this);
  }

  connectedCallback() {
    this.trigger?.addEventListener('click', this.handleTriggerClick);
    this.handleBreakpointChange();

    if (typeof mediaQueryLarge.addEventListener === 'function') {
      mediaQueryLarge.addEventListener('change', this.handleBreakpointChange);
    } else if (typeof mediaQueryLarge.addListener === 'function') {
      mediaQueryLarge.addListener(this.handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.handleTriggerClick);
    this.removeDesktopListeners();

    if (typeof mediaQueryLarge.removeEventListener === 'function') {
      mediaQueryLarge.removeEventListener('change', this.handleBreakpointChange);
    } else if (typeof mediaQueryLarge.removeListener === 'function') {
      mediaQueryLarge.removeListener(this.handleBreakpointChange);
    }
  }

  get trigger() {
    return this.querySelector('.hw-media-hotspot__trigger');
  }

  get dialog() {
    return this.querySelector('.hw-media-hotspot__dialog');
  }

  handleBreakpointChange() {
    this.removeDesktopListeners();

    if (!mediaQueryLarge.matches || isTouchDevice()) return;

    this.setupDesktopListeners();
  }

  setupDesktopListeners() {
    const trigger = this.trigger;
    const dialog = this.dialog;
    if (!(trigger instanceof HTMLButtonElement) || !(dialog instanceof HTMLDialogElement)) {
      return;
    }

    const pointerenterHandler = () => {
      if (dialog.open) return;

      this.timer = window.setTimeout(() => {
        this.showDialog();
      }, 120);

      trigger.addEventListener('pointerleave', this.handlePointerLeave);
    };

    this.pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);
  }

  removeDesktopListeners() {
    const trigger = this.trigger;
    const dialog = this.dialog;

    if (this.pointerenterHandler && trigger instanceof HTMLButtonElement) {
      trigger.removeEventListener('pointerenter', this.pointerenterHandler);
      trigger.removeEventListener('pointerleave', this.handlePointerLeave);
      if (dialog instanceof HTMLDialogElement) {
        dialog.removeEventListener('pointerleave', this.handlePointerLeave);
      }
      this.pointerenterHandler = null;
    }

    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * @returns {Promise<void> | undefined}
   */
  calculateDialogPlacement() {
    const trigger = this.trigger;
    const dialog = this.dialog;
    const hotspotBounds = this.parentElement;

    if (
      !(hotspotBounds instanceof HTMLElement) ||
      !(trigger instanceof HTMLButtonElement) ||
      !(dialog instanceof HTMLDialogElement)
    ) {
      return;
    }

    const buttonGap = 10;
    const containerGap = 10;
    const totalGap = buttonGap + containerGap;

    const containerRect = hotspotBounds.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');

    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    const spaceRight = containerRect.width - buttonRight - containerGap;
    const spaceLeft = buttonLeft - containerGap;

    let horizontalPlacement = 'right';

    if (spaceRight >= dialogWidth + buttonGap) {
      horizontalPlacement = 'right';
    } else if (spaceLeft >= dialogWidth + buttonGap) {
      horizontalPlacement = 'left';
    } else {
      horizontalPlacement = 'center';
    }

    let verticalPlacement = 'bottom';
    let verticalOffset = 0;

    if (horizontalPlacement !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - containerGap) {
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        verticalPlacement = 'top';

        if (dialogStartY < containerGap) {
          verticalOffset = containerGap - dialogStartY;
        } else if (dialogEndY > containerRect.height - containerGap) {
          verticalOffset = -(dialogEndY - (containerRect.height - containerGap));
        }
      } else if (dialogStartY < containerGap) {
        verticalOffset = containerGap - dialogStartY;
      }
    } else if (containerRect.height - buttonBottom >= dialogHeight + totalGap) {
      verticalPlacement = 'bottom';
    } else if (buttonTop >= dialogHeight + totalGap) {
      verticalPlacement = 'top';
    } else {
      verticalPlacement = buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
    }

    dialog.dataset.placement = `${horizontalPlacement},${verticalPlacement}`;

    if (verticalOffset !== 0) {
      dialog.style.setProperty('--hw-media-hotspot-dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--hw-media-hotspot-dialog-vertical-offset');
    }

    return new Promise((resolve) => {
      window.setTimeout(resolve, 40);
    });
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerLeave = (event) => {
    const dialog = this.dialog;
    const trigger = this.trigger;
    if (!(dialog instanceof HTMLDialogElement) || !(trigger instanceof HTMLButtonElement)) {
      return;
    }

    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    if (!dialog.open) return;

    const isLeavingTrigger = event.target === trigger;
    const isLeavingDialog = event.target === dialog;
    const isGoingToDialog =
      event.relatedTarget === dialog ||
      (event.relatedTarget instanceof Element &&
        event.relatedTarget.closest('dialog') === dialog);
    const isGoingToTrigger = event.relatedTarget === trigger;

    if (
      (isLeavingTrigger && !isGoingToDialog) ||
      (isLeavingDialog && !isGoingToTrigger)
    ) {
      this.closeDialog();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  handleTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.dialog?.open) {
      this.closeDialog();
      return;
    }

    this.showDialog();
  };

  showDialog = async () => {
    const dialog = this.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;
    const allHotspots = document.querySelectorAll('hw-media-hotspot-component');

    for (const hotspot of allHotspots) {
      if (!(hotspot instanceof HwMediaHotspotComponent) || hotspot === this) continue;
      if (hotspot.dialog?.open) {
        hotspot.closeDialog();
      }
    }

    await this.calculateDialogPlacement();
    dialog.dataset.showing = 'true';
    dialog.show();
    document.body.addEventListener('click', this.lightDismissMouse);
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);
    dialog.addEventListener('pointerleave', this.handlePointerLeave);
  };

  closeDialog = async () => {
    const dialog = this.dialog;
    const trigger = this.trigger;
    if (!(dialog instanceof HTMLDialogElement) || !(trigger instanceof HTMLButtonElement)) {
      return;
    }

    if (!dialog.open) return;

    dialog.close();
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    dialog.removeEventListener('pointerleave', this.handlePointerLeave);
    trigger.removeEventListener('pointerleave', this.handlePointerLeave);

    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((animation) => animation.finished));

    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.placement;
      dialog.style.removeProperty('--hw-media-hotspot-dialog-vertical-offset');
    }
  };

  /**
   * @param {MouseEvent} event
   */
  lightDismissMouse = (event) => {
    const dialog = this.dialog;
    const trigger = this.trigger;
    if (!(dialog instanceof HTMLDialogElement) || !(trigger instanceof HTMLButtonElement)) {
      return;
    }

    const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (!eventPath.includes(dialog) && !eventPath.includes(trigger)) {
      this.closeDialog();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  lightDismissKeyboard = (event) => {
    const dialog = this.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' &&
        !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}

if (!customElements.get('hw-media-hotspot-component')) {
  customElements.define('hw-media-hotspot-component', HwMediaHotspotComponent);
}

/**
 * @typedef {Object} HwMediaHotspotsRootState
 * @property {HTMLElement | null | undefined} galleryInstance
 * @property {HTMLElement | null | undefined} galleryMedia
 * @property {MutationObserver | null | undefined} observer
 * @property {ResizeObserver | null | undefined} resizeObserver
 * @property {number | null | undefined} syncFrame
 */

/** @type {WeakMap<HTMLElement, HwMediaHotspotsRootState>} */
const rootState = new WeakMap();

/** @type {WeakSet<HTMLElement>} */
const initializedRoots = new WeakSet();

const getTemplate = (root) => {
  const template = root.querySelector('[data-hw-media-hotspots-template]');
  return template instanceof HTMLTemplateElement ? template : null;
};

const getState = (root) => {
  const state = rootState.get(root);

  if (state) return state;

  const nextState = {
    galleryInstance: null,
    galleryMedia: null,
    observer: null,
    resizeObserver: null,
    syncFrame: null,
  };

  rootState.set(root, nextState);

  return nextState;
};

const ensureInstance = (root, instanceName) => {
  const state = getState(root);
  const stateKey = 'galleryInstance';

  if (state[stateKey] instanceof HTMLElement) {
    return state[stateKey];
  }

  const template = getTemplate(root);
  if (!template) return null;

  const fragment = template.content.cloneNode(true);
  const instance = fragment.firstElementChild;

  if (!(instance instanceof HTMLElement)) return null;

  instance.dataset.hwMediaHotspotsInstance = instanceName;
  state[stateKey] = instance;

  return instance;
};

const closeOpenDialogs = (instance) => {
  for (const dialog of instance.querySelectorAll('dialog[open]')) {
    if (!(dialog instanceof HTMLDialogElement)) continue;
    dialog.close();
    delete dialog.dataset.showing;
    delete dialog.dataset.placement;
    dialog.style.removeProperty('--hw-media-hotspot-dialog-vertical-offset');
  }
};

const positionInstance = (instance, parent, target) => {
  const parentRect = parent.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();

  instance.style.setProperty(
    '--hw-media-hotspots-overlay-top',
    `${targetRect.top - parentRect.top}px`
  );
  instance.style.setProperty(
    '--hw-media-hotspots-overlay-left',
    `${targetRect.left - parentRect.left}px`
  );
  instance.style.setProperty(
    '--hw-media-hotspots-overlay-width',
    `${targetRect.width}px`
  );
  instance.style.setProperty(
    '--hw-media-hotspots-overlay-height',
    `${targetRect.height}px`
  );
};

const isHotspotTargetDisplayed = (el) => {
  if (!(el instanceof HTMLElement)) return false;
  const rect = el.getBoundingClientRect();
  /* Grid <li> nodes stay in DOM with display:none on mobile (carousel is shown). */
  return rect.width > 1 && rect.height > 1;
};

const getGalleryTarget = (root) => {
  const gallery = root.closest('.hw-product-media-gallery');
  const galleryMedia = gallery?.querySelector(
    '.hw-product-media-gallery__gallery-media'
  );

  if (!(galleryMedia instanceof HTMLElement)) {
    return { parent: null, target: null };
  }

  /* Hotspots stay on the first gallery *image* in sort order (DOM), not the active carousel slide. */
  const gridImage = galleryMedia.querySelector(
    '.media-gallery__grid > .product-media-container--image'
  );
  if (gridImage instanceof HTMLElement && isHotspotTargetDisplayed(gridImage)) {
    return { parent: galleryMedia, target: gridImage };
  }

  const firstCarouselImage = galleryMedia.querySelector(
    'slideshow-component slideshow-slide.product-media-container--image'
  );
  if (firstCarouselImage instanceof HTMLElement) {
    return { parent: galleryMedia, target: firstCarouselImage };
  }

  const fallback = galleryMedia.querySelector('.product-media-container--image');
  return { parent: galleryMedia, target: fallback };
};

/**
 * Hide hotspots while the carousel is moving or when the pinned slide is not active.
 * @param {HTMLElement | null} target
 * @returns {boolean}
 */
const isHotspotOverlayVisible = (target) => {
  if (!(target instanceof HTMLElement)) return false;

  if (!target.matches('slideshow-slide')) return true;

  const slideshow = target.closest('slideshow-component');
  if (!(slideshow instanceof HTMLElement)) return true;

  if (slideshow.hasAttribute('dragging') || slideshow.hasAttribute('transitioning')) {
    return false;
  }

  const slides = slideshow.slides;
  const current =
    typeof slideshow.current === 'number' ? slideshow.current : undefined;

  if (!Array.isArray(slides) || current === undefined) return true;

  const hotspotIndex = slides.indexOf(target);
  if (hotspotIndex < 0) return true;

  return hotspotIndex === current;
};

const mountInstance = (root, instanceName, parent, target) => {
  const instance = ensureInstance(root, instanceName);

  if (!(instance instanceof HTMLElement)) return;

  if (!(parent instanceof HTMLElement) || !(target instanceof HTMLElement)) {
    closeOpenDialogs(instance);
    instance.remove();
    return;
  }

  if (instance.parentElement !== parent) {
    closeOpenDialogs(instance);
    parent.append(instance);
  }

  positionInstance(instance, parent, target);
};

const syncRoot = (root) => {
  if (!root.isConnected) return;

  const galleryTarget = getGalleryTarget(root);
  const { parent, target } = galleryTarget;
  const instance = ensureInstance(root, 'gallery');

  if (!(instance instanceof HTMLElement)) return;

  if (!(parent instanceof HTMLElement) || !(target instanceof HTMLElement)) {
    closeOpenDialogs(instance);
    instance.remove();
    return;
  }

  if (!isHotspotOverlayVisible(target)) {
    closeOpenDialogs(instance);
    instance.setAttribute('hidden', '');
    instance.setAttribute('aria-hidden', 'true');
    if (instance.parentElement !== parent) {
      parent.append(instance);
    }
    return;
  }

  instance.removeAttribute('hidden');
  instance.removeAttribute('aria-hidden');
  mountInstance(root, 'gallery', parent, target);
};

const requestSync = (root) => {
  const state = getState(root);

  if (state.syncFrame) return;

  state.syncFrame = window.requestAnimationFrame(() => {
    state.syncFrame = null;
    syncRoot(root);
  });
};

const initRoot = (root) => {
  if (initializedRoots.has(root)) return;

  initializedRoots.add(root);

  const state = getState(root);
  const gallery = root.closest('.hw-product-media-gallery');
  const galleryMedia = gallery?.querySelector(
    '.hw-product-media-gallery__gallery-media'
  );

  if (galleryMedia instanceof HTMLElement) {
    state.galleryMedia = galleryMedia;
    galleryMedia.addEventListener(
      'scroll',
      () => {
        requestSync(root);
      },
      true
    );

    state.observer = new MutationObserver(() => {
      requestSync(root);
    });
    state.observer.observe(galleryMedia, {
      attributes: true,
      attributeFilter: [
        'class',
        'open',
        'style',
        'hidden',
        'transitioning',
        'dragging',
      ],
      childList: true,
      subtree: true,
    });

    if (typeof ResizeObserver !== 'undefined') {
      state.resizeObserver = new ResizeObserver(() => {
        requestSync(root);
      });
      state.resizeObserver.observe(galleryMedia);
    }
  }

  requestSync(root);
};

const initRoots = (scope = document) => {
  if (!(scope instanceof Document || scope instanceof Element)) return;

  const roots = scope.querySelectorAll('[data-hw-media-hotspots-root]');

  for (const root of roots) {
    if (!(root instanceof HTMLElement)) continue;
    initRoot(root);
  }
};

initRoots();

window.addEventListener('resize', () => {
  initRoots();

  for (const root of document.querySelectorAll('[data-hw-media-hotspots-root]')) {
    if (!(root instanceof HTMLElement)) continue;
    requestSync(root);
  }
});

document.addEventListener('slideshow:select', () => {
  for (const root of document.querySelectorAll('[data-hw-media-hotspots-root]')) {
    if (!(root instanceof HTMLElement)) continue;
    requestSync(root);
  }
});

if (document.body instanceof HTMLElement) {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (node.matches('[data-hw-media-hotspots-root]')) {
          initRoot(node);
        }

        initRoots(node);
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}
